/**
 * The session's control row (D153, split from `session-view.tsx` at the
 * file-size cap): the references behind buttons — map, journal, voices —
 * the settings gear (D172) that holds the optional-layer switches — images,
 * sound, clock, and (D209) the map's style where the pack offers more than
 * one — and (D242) Forget the Record where the pack keeps one — and the
 * export, and the account.
 *
 * @remarks
 * Every control here is the shell's, not the engine's: each one reads a
 * derived view or a preference and dispatches a preference change or a
 * download. The row renders only once the session has something to refer
 * to (the view passes the derived pieces in, already computed). What the
 * settings panel holds is decided HERE, because only the shell knows which
 * layers the pack declares — the panel itself is wordless furniture.
 *
 * @packageDocumentation
 */

import type { GameDescription, SessionView } from '@orpheus/api-contracts';

import {
	ambientAudioPreferenceSet,
	ambientAudioVolumeSet,
	autoWaitPreferenceSet,
	forgetRecord,
	type GameStore,
	mapStylePreferenceSet,
	roomImagesPreferenceSet,
} from '@orpheus/game-store';
import { layOutMapView, type MapStylePreference, type PlayHost } from '@orpheus/player-host';
import { isWebGlAvailable } from '@orpheus/player-scene/support';
import {
	AmbientAudioToggleComponent,
	AmbientVolumeSliderComponent,
	ClockToggleComponent,
	COMMAND_INPUT_FIELD_ID,
	ForgetRecordControlComponent,
	JournalDialogComponent,
	MapDialogComponent,
	MapStyleChooserComponent,
	RoomImageToggleComponent,
	SCENE_STAND_IN_NOTICE,
	SettingsControlComponent,
	VoicesDialogComponent,
} from '@orpheus/player-ui';
import { type ReactNode, useMemo } from 'react';

import { downloadTranscriptExport } from './export-transcript.js';
import { useIsBelowRailBreakpoint } from './rail-breakpoint.js';
import { renderSceneFigure } from './scene-figure.js';

/**
 * Send focus back to the directive line once a reference dialog has closed.
 *
 * @remarks
 * The native `<dialog>` restores focus to the button that opened it, which
 * is right for a settings panel and wrong for a reference: a player who
 * looked at the map wants to type the move they just planned, not tab off
 * the Map button first (Mike, 2026-09-10). The field is found by the id it
 * exports rather than by a ref threaded through three components, because
 * the row and the input line are siblings that share nothing else. Absent
 * (the session ended, the line unmounted) or disabled (a turn in flight),
 * `focus()` is a no-op and the native restoration stands.
 */
const returnFocusToDirective = (): void => {
	document.getElementById(COMMAND_INPUT_FIELD_ID)?.focus();
};

/** Props for {@link SessionControls}. */
export interface SessionControlsProps {
	/** The account control (D157), when the build has an identity Worker to talk to; absent, the row has no such button. */
	readonly account?: ReactNode;
	readonly ambientAudioVolume: number;
	/** The game around the session: whether it has sound, a clock, a Record. */
	readonly game: GameDescription;
	/** The host — the export asks it for the transcript (D156). */
	readonly host: PlayHost;
	readonly isAmbientAudioEnabled: boolean;
	readonly isClockEnabled: boolean;
	readonly isRoomImageryEnabled: boolean;
	/** D209: which of the pack's offered map styles the player looks at. */
	readonly mapStylePreference: MapStylePreference;
	readonly store: GameStore;
	/** The view: the map, the journal, the voices, and whether the room shows a plate. */
	readonly view: SessionView;
}

/**
 * Render the control row.
 *
 * @param props - {@link SessionControlsProps}
 * @returns The row
 */
export const SessionControls = ({
	account,
	ambientAudioVolume,
	game,
	host,
	isAmbientAudioEnabled,
	isClockEnabled,
	isRoomImageryEnabled,
	mapStylePreference,
	store,
	view,
}: SessionControlsProps) => {
	// D210: whether this browser can draw a scene, asked once — the probe
	// makes a canvas, and the answer does not change while the page lives.
	const isSceneDrawable = useMemo(() => isWebGlAvailable(), []);
	// D224: at a phone's width the hologram follows the scene's answer to
	// crowding, and the layout needs telling — the same query the stylesheet
	// draws the narrow shell by.
	const isNarrow = useIsBelowRailBreakpoint();
	// D209: the host describes the map and the shell draws it, in the style
	// the player chose among those the pack offers — so a change of style is
	// a re-layout here and never a round trip. Memoized on the inputs that
	// can change it: the row re-renders on every store tick. D210: a chosen
	// scene this browser cannot draw is laid out as the hologram, and the
	// panel says so.
	const map = useMemo(
		() => layOutMapView(view.map, mapStylePreference, { isNarrow, isSceneDrawable }),
		[view.map, mapStylePreference, isNarrow, isSceneDrawable],
	);

	return (
		<div className="session-view__controls">
			<MapDialogComponent
				annotations={map.annotations}
				// D211: the map's style is chosen ON the map (Mike's ruling) — and
				// only where the pack offers a choice: one offered style is a lock
				// (D209: the list is the permission), and a chooser of one would be
				// furniture.
				chooser={
					view.map.styles.length > 1 ? (
						<MapStyleChooserComponent
							chosen={map.style}
							offered={view.map.styles}
							onChoose={(style) => {
								store.dispatch(mapStylePreferenceSet(style));
							}}
						/>
					) : undefined
				}
				layout={map.layout}
				notice={map.isSceneStandIn ? SCENE_STAND_IN_NOTICE : undefined}
				renderScene={renderSceneFigure}
				onDismissed={returnFocusToDirective}
				title={view.map.title}
				visitedCount={view.map.visitedCount}
			/>
			<JournalDialogComponent entries={view.journal} onDismissed={returnFocusToDirective} />
			{/* D192: a pack with no declared speaker has no voices to index — no door. */}
			{view.speakers.length > 0 && <VoicesDialogComponent onDismissed={returnFocusToDirective} speakers={view.speakers} voices={view.voices} />}
			{/* D172: everything a player SETS, behind one gear — the row on a phone
		    has room for five buttons, and these three plus the export made eight.
		    The rows render only for the layers the pack declares, so a pack with
		    no sound and no clock discloses the plate switch and the export alone. */}
			<SettingsControlComponent isSoundMuted={!!game.hasAudio && !isAmbientAudioEnabled}>
				{/* The plate is optional by ruling, not by accident: a parser
			    game must stay whole as pure text. Off, it is not rendered
			    at all, so the transcript takes back its space. */}
				{view.scene === undefined ? undefined : (
					<RoomImageToggleComponent
						isImageryEnabled={isRoomImageryEnabled}
						onToggle={(isImageryEnabled) => {
							store.dispatch(roomImagesPreferenceSet(isImageryEnabled));
						}}
					/>
				)}
				{/* R35: the ear's own control, a sibling of the eye's (§8.1) —
			    rendered whenever the pack declares an audio layer: the mute
			    switch, and under it the loudness lever (D116). */}
				{game.hasAudio ? (
					<>
						<AmbientAudioToggleComponent
							isAudioEnabled={isAmbientAudioEnabled}
							onToggle={(isAudioEnabled) => {
								store.dispatch(ambientAudioPreferenceSet(isAudioEnabled));
							}}
						/>
						<AmbientVolumeSliderComponent
							onVolumeChange={(volume) => {
								store.dispatch(ambientAudioVolumeSet(volume));
							}}
							volume={ambientAudioVolume}
						/>
					</>
				) : undefined}
				{/* D153: the clock's own control — rendered whenever the pack
			    declares a clock, so the limit is adjustable from inside the
			    session as well as before it. */}
				{game.clock === undefined ? undefined : (
					<ClockToggleComponent
						isClockEnabled={isClockEnabled}
						onToggle={(isEnabled) => {
							store.dispatch(autoWaitPreferenceSet(isEnabled ? 'on' : 'off'));
						}}
					/>
				)}
				{/* D242: the Record's own control — rendered only for a pack that
			    keeps one (D241), beside the game's other preferences. It asks
			    once before it clears anything; the host clears the device and,
			    signed in, the account. */}
				{game.hasRecord === true ? (
					<ForgetRecordControlComponent
						onForget={async () => {
							await store.dispatch(forgetRecord());
						}}
					/>
				) : undefined}
				{/* The export, under a rule: an action among settings, but the
			    once-a-session one, and this is the one place with room for it
			    on a phone — the row had hidden it there outright. D204: the rule
			    only when a setting sits above it — a pack with no plate, no
			    sound and no clock discloses the export alone, and a separator
			    with nothing on its other side is a bug in miniature (Mike,
			    2026-09-25). */}
				{view.scene !== undefined || game.hasAudio || game.clock !== undefined || game.hasRecord === true ? (
					<hr aria-hidden="true" className="settings-control__rule" />
				) : undefined}
				<button
					className="session-view__export"
					onClick={() => {
					// R20/D86: the export — serialized from the record alone, by the
					// host that holds it; the download mechanics live in
					// export-transcript.ts (moved R35 at the file-size cap).
						void downloadTranscriptExport(host, game, store.getState().session.seed);
					}}
					type="button"
				>
					<svg
						aria-hidden="true"
						className="session-view__export-icon"
						fill="none"
						focusable="false"
						height="16"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.6}
						viewBox="0 0 24 24"
						width="16"
					>
						<path d="M12 3v12" />
						<path d="m7 10 5 5 5-5" />
						<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
					</svg>
					<span className="session-view__export-label">Export</span>
				</button>
			</SettingsControlComponent>
			{/* D157: who the player is — a door in, or the account — last in the
		    row, because it is about the player rather than the world.
		    Rendered by the shell, which alone knows whether identity exists. */}
			{account}
		</div>
	);
};
